import * as Menu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronDown } from "lucide-react";
import { Children, isValidElement, useId, type ChangeEvent, type ReactElement, type ReactNode, type SelectHTMLAttributes } from "react";
import { useWeltAktiv } from "../welt/WeltWurzel";
import { useGrosseSchrift } from "./grosseSchrift";

type Option = { wert: string; name: string };

/** Einträge aus den `<option>`-Kindern (Wert und Text), in derselben Reihenfolge */
function optionen(kinder: ReactNode): Option[] {
  return Children.toArray(kinder)
    .filter((k): k is ReactElement<{ value?: unknown; children?: ReactNode }> => isValidElement(k) && k.type === "option")
    .map((k) => ({ wert: String(k.props.value ?? ""), name: Children.toArray(k.props.children).join("") }));
}

/** W82 Web-6a-N1 (WN-20, D-1148, wie iOS `PGNamensAuswahl`): Auswahlfeld einer Zeile. Trägt es `data-aktenwahl` (Akte in „Neue
 *  Frist“, „Angaben ändern“ und „Prüfen“) und ist die Schrift groß (ab 150 %, `useGrosseSchrift`), steht statt der nativen Auswahl
 *  ein Knopf mit dem ganzen gewählten Namen, mehrzeilig mit Silbentrennung, und öffnet dieselben Einträge als Einfachauswahl
 *  (Menü wie `Menue.tsx`); sonst die native Auswahl wie bisher. Der Knopf nennt Beschriftung und Wert („Zuordnen, Keine Akte“),
 *  trägt Beschreibung, `aria-invalid`, Rand und Fokusring wie das Feld; Escape schließt nur das Menü, der Fokus kehrt auf den Knopf
 *  zurück. Im DOM steht je Zustand genau ein Bedienelement. */
export function WahlFeld(props: SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: ReactNode }) {
  const { label, children, ...rest } = props;
  const gross = useGrosseSchrift();
  const welt = useWeltAktiv();
  const wertId = useId();
  const namen = (rest as Record<string, unknown>)["data-aktenwahl"] !== undefined;
  if (!namen || !gross) {
    return <select {...rest}>{children}</select>;
  }
  const liste = optionen(children);
  const wert = String(rest.value ?? "");
  const name = liste.find((o) => o.wert === wert)?.name ?? "";
  const waehlen = (neu: string) => {
    if (neu === wert) return;
    const ziel = { value: neu, name: rest.name ?? "" };
    rest.onChange?.({ target: ziel, currentTarget: ziel, type: "change" } as unknown as ChangeEvent<HTMLSelectElement>);
  };
  return (
    <Menu.Root>
      <Menu.Trigger asChild disabled={rest.disabled}>
        <button
          type="button"
          id={rest.id}
          aria-label={`${label}, ${name}`}
          aria-describedby={rest["aria-describedby"]}
          aria-invalid={rest["aria-invalid"]}
          data-aktenwahl=""
          data-namens-knopf=""
          className={`namens-knopf ${rest.className ?? ""} flex items-center gap-2 py-2 text-left`}
        >
          <span id={wertId} lang="de" className="min-w-0 flex-1 break-words hyphens-auto">
            {name}
          </span>
          <ChevronDown size={20} strokeWidth={1.75} aria-hidden className="shrink-0 text-pg-text-2" />
        </button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          aria-label={label}
          data-welt={welt ? "" : undefined}
          data-schwebend={welt ? "" : undefined}
          className={`menue namens-menue z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-(--radix-dropdown-menu-trigger-width) overflow-y-auto py-1 ${welt ? "menue-welt" : "mat-thick rounded-lg shadow-float"}`}
        >
          <Menu.RadioGroup value={wert} onValueChange={waehlen}>
            {liste.map((o) => (
              <Menu.RadioItem
                key={o.wert}
                value={o.wert}
                className="menue-eintrag t-body flex min-h-(--touch) cursor-default items-center gap-3 px-4 py-2 outline-none data-highlighted:bg-pg-surface-2"
              >
                <span lang="de" className="min-w-0 flex-1 break-words hyphens-auto">
                  {o.name}
                </span>
                <span className="grid w-5 shrink-0 place-items-center">
                  <Menu.ItemIndicator>
                    <Check size={18} strokeWidth={2} aria-hidden />
                  </Menu.ItemIndicator>
                </span>
              </Menu.RadioItem>
            ))}
          </Menu.RadioGroup>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
